/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Grid, ToggleButton, ToggleButtonGroup } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BaseService from '../../../services/BaseService';
import AdereByMalhaChart from './AdereByMalhaChart';
import VencimentoTimelineChart from './VencimentoTimelineChart';
import ChartFocusDialog from './ChartFocusDialog';
import {
  ChartCard,
  formatAdereMonthLabel,
  normalizeAdereChartResponse,
  normalizeVencimentoChartResponse,
} from './chartShared';
import { VENCIMENTO_MODES } from './constants';

const FOCUS_HEIGHT = 520;

const ChartsRow = ({
  apiFilterBase,
  refreshKey,
  selectedAdereMonth,
  onAdereMonthChange,
  onAdereSegmentSelect,
  activeVencimentoUrgency,
  onVencimentoUrgencyChange,
}) => {
  const [adereItems, setAdereItems] = useState([]);
  const [adereStatusOrder, setAdereStatusOrder] = useState(null);
  const [adereLoading, setAdereLoading] = useState(false);
  const [vencItems, setVencItems] = useState([]);
  const [vencSummary, setVencSummary] = useState(null);
  const [vencWindow, setVencWindow] = useState(null);
  const [vencAlerts, setVencAlerts] = useState([]);
  const [vencLoading, setVencLoading] = useState(false);
  const [vencMode, setVencMode] = useState(VENCIMENTO_MODES.NEXT_7_DAYS);
  const [focusAdere, setFocusAdere] = useState(false);
  const [focusVenc, setFocusVenc] = useState(false);

  const adereFilter = useMemo(
    () => ({
      ...apiFilterBase,
      possuiAdere: 'true',
    }),
    [apiFilterBase],
  );

  const vencFilter = useMemo(
    () => ({
      ...apiFilterBase,
      mode: vencMode,
    }),
    [apiFilterBase, vencMode],
  );

  const fetchAdere = useCallback(async () => {
    setAdereLoading(true);
    try {
      const response = await BaseService.post({
        url: 'reports/inspecao/charts/adere-by-malha',
        data: adereFilter,
      });
      const { items, statusOrder } = normalizeAdereChartResponse(response?.data);
      setAdereItems(items);
      setAdereStatusOrder(statusOrder);
    } catch {
      setAdereItems([]);
      setAdereStatusOrder(null);
    } finally {
      setAdereLoading(false);
    }
  }, [adereFilter, refreshKey]);

  const fetchVencimento = useCallback(async () => {
    setVencLoading(true);
    try {
      const response = await BaseService.post({
        url: 'reports/inspecao/charts/vencimento-timeline',
        data: vencFilter,
      });
      const normalized = normalizeVencimentoChartResponse(
        response?.data,
        vencMode,
        apiFilterBase?.inicio,
        apiFilterBase?.fim,
      );
      setVencItems(normalized.items);
      setVencSummary(normalized.summary);
      setVencWindow(normalized.window);
      setVencAlerts(normalized.alerts);
    } catch {
      setVencItems([]);
      setVencSummary(null);
      setVencWindow(null);
      setVencAlerts([]);
    } finally {
      setVencLoading(false);
    }
  }, [vencFilter, refreshKey, vencMode, apiFilterBase?.inicio, apiFilterBase?.fim]);

  useEffect(() => {
    fetchAdere();
  }, [fetchAdere]);

  useEffect(() => {
    fetchVencimento();
  }, [fetchVencimento]);

  const handleAlertClick = useCallback(
    (alert) => {
      if (alert?.bucket) {
        onVencimentoUrgencyChange?.(alert.bucket);
      }
    },
    [onVencimentoUrgencyChange],
  );

  const adereTitle = selectedAdereMonth
    ? `${formatAdereMonthLabel(selectedAdereMonth)} — por malha`
    : 'Adere por mês';

  const adereBackAction = selectedAdereMonth ? (
    <Button
      size="small"
      variant="outlined"
      startIcon={<ArrowBackIcon />}
      onClick={() => onAdereMonthChange?.(null)}
    >
      Voltar
    </Button>
  ) : null;

  const adereChartProps = {
    items: adereItems,
    statusOrder: adereStatusOrder,
    loading: adereLoading,
    selectedAdereMonth,
    onAdereMonthChange,
    onSegmentSelect: onAdereSegmentSelect,
  };

  const vencChartProps = {
    items: vencItems,
    summary: vencSummary,
    window: vencWindow,
    alerts: vencAlerts,
    loading: vencLoading,
    activeUrgency: activeVencimentoUrgency,
    onUrgencyChange: onVencimentoUrgencyChange,
    onAlertClick: handleAlertClick,
  };

  const vencModeToggle = (
    <ToggleButtonGroup
      size="small"
      exclusive
      value={vencMode}
      onChange={(_, value) => {
        if (value) setVencMode(value);
      }}
    >
      <ToggleButton value={VENCIMENTO_MODES.NEXT_7_DAYS}>7 dias</ToggleButton>
      <ToggleButton value={VENCIMENTO_MODES.FILTER_RANGE}>Período</ToggleButton>
    </ToggleButtonGroup>
  );

  return (
    <>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <ChartCard
            title={adereTitle}
            action={adereBackAction}
            onExpand={() => setFocusAdere(true)}
            contentSx={{ pl: 0.5, pr: 1, pt: 0, '&:last-child': { pb: 2 } }}
          >
            <AdereByMalhaChart {...adereChartProps} />
          </ChartCard>
        </Grid>
        <Grid size={{ xs: 12, lg: 4 }}>
          <ChartCard
            title="Vencimentos no tempo"
            action={vencModeToggle}
            onExpand={() => setFocusVenc(true)}
            contentSx={{ pl: 1, pr: 2, pb: 1 }}
          >
            <VencimentoTimelineChart {...vencChartProps} />
          </ChartCard>
        </Grid>
      </Grid>

      <ChartFocusDialog
        open={focusAdere}
        title={adereTitle}
        action={adereBackAction}
        onClose={() => setFocusAdere(false)}
      >
        <AdereByMalhaChart {...adereChartProps} height={FOCUS_HEIGHT} />
      </ChartFocusDialog>

      <ChartFocusDialog
        open={focusVenc}
        title="Vencimentos no tempo"
        action={vencModeToggle}
        onClose={() => setFocusVenc(false)}
      >
        <VencimentoTimelineChart
          {...vencChartProps}
          height={FOCUS_HEIGHT}
          expanded
        />
      </ChartFocusDialog>
    </>
  );
};

ChartsRow.propTypes = {
  apiFilterBase: PropTypes.object.isRequired,
  refreshKey: PropTypes.bool,
  selectedAdereMonth: PropTypes.string,
  onAdereMonthChange: PropTypes.func,
  onAdereSegmentSelect: PropTypes.func,
  activeVencimentoUrgency: PropTypes.string,
  onVencimentoUrgencyChange: PropTypes.func,
};

ChartsRow.defaultProps = {
  refreshKey: false,
  selectedAdereMonth: null,
  onAdereMonthChange: null,
  onAdereSegmentSelect: null,
  activeVencimentoUrgency: null,
  onVencimentoUrgencyChange: null,
};

export default ChartsRow;
